/**
 * The message an adviser sends their client with the link.
 *
 * It lives here, not inside the dialog, for two reasons. It is written in the
 * **client's** language — the one the link carries — not the adviser's
 * interface language, so react-intl's current locale is the wrong source and it
 * cannot be a normal message id. And as three template literals buried in JSX
 * it was the only client-facing text in the product that nobody could review
 * without reading a component.
 *
 * **The product noun appears once per language in the message, and once in the
 * share title** (`小錦囊` / `travel kit`). Renaming it is six edits in this file
 * and nowhere else.
 *
 * Tone is deliberate: warm and practical, a trip-planning gift rather than a
 * "just in case something goes wrong" sheet. It leads with what the client uses
 * before leaving (packing checklist, plug and voltage), lists the emergency
 * contacts last, and closes by inviting them to forward it to the people
 * travelling with them. Laid out in short lines with a bullet list so it reads
 * at a glance in a chat bubble — the line breaks survive WhatsApp and the
 * share sheet.
 */

/**
 * The title handed to the share sheet with the message. Mail and Gmail use it
 * as the subject the client reads, so it is the client's, in the link's
 * language — not the adviser's dialog heading (「製作旅行小錦囊」).
 *
 * @param {'tc'|'sc'|'en'} lang  the link's language, not the adviser's
 */
export function buildClientShareTitle(lang) {
  if (lang === 'en') return 'Your travel kit ✈️';
  if (lang === 'sc') return '你的专属旅行小锦囊 ✈️';
  return '你的專屬旅行小錦囊 ✈️';
}

/**
 * @param {object} params
 * @param {'tc'|'sc'|'en'} params.lang     the link's language, not the adviser's
 * @param {string} params.clientName       already trimmed; may be empty
 * @param {string} params.countryName      destination, named in `lang`; empty
 *                                         reads as a trip with no place named
 * @param {string} params.url              the full SOS link
 */
export function buildClientMessage({ lang, clientName, countryName, url }) {
  if (lang === 'en') {
    const greeting = clientName ? `${clientName}, ` : '';
    const trip = countryName ? `off to ${countryName}` : 'going travelling';
    return [
      `👋🏻${greeting}I heard you're ${trip}, so I've put together your own travel kit ✈️`,
      `Inside you'll find:`,
      `* Packing checklist`,
      `* Local plugs and voltage`,
      `* Emergency contacts...`,
      ``,
      `🔗 ${url}`,
      `💡Open it whenever you need it, and feel free to forward it to anyone travelling with you`
    ].join('\n');
  }

  if (lang === 'sc') {
    const greeting = clientName ? `${clientName}，` : '';
    const trip = countryName ? `去${countryName}玩` : '去旅行';
    return [
      `👋🏻${greeting}知道你要${trip}，我整理了你的专属小锦囊 ✈️`,
      `里面包括：`,
      `* 行李清单`,
      `* 当地插头与电压`,
      `* 紧急联络资料...`,
      ``,
      `🔗 ${url}`,
      `💡有需要随时打开，记得可转发给同行亲友`
    ].join('\n');
  }

  const greeting = clientName ? `${clientName}，` : '';
  const trip = countryName ? `去${countryName}玩` : '去旅行';
  return [
    `👋🏻${greeting}知道你要${trip}，我整理了你的專屬小錦囊 ✈️`,
    `裡面包括：`,
    `* 行李清單`,
    `* 當地插頭與電壓`,
    `* 緊急聯絡資料...`,
    ``,
    `🔗 ${url}`,
    `💡有需要隨時打開，記得可轉發給同行親友`
  ].join('\n');
}
